import React, { useState } from "react";
import { creerOuReinitialiserAcces, desactiverAcces } from "../services/auth/accesService";
import "../styles/acces.css";

/**
 * Accès à la plateforme d'un formateur ou d'un apprenant (point 8 de l'équipe).
 *
 * Crée le compte relié à la fiche, avec un mot de passe provisoire affiché UNE
 * fois (à remettre à la personne, qui devra le changer à sa première
 * connexion), ou le réinitialise, ou désactive l'accès.
 *
 * props : base ('apprenants' | 'formateurs'), ficheId, email, acces (état
 * renvoyé par l'API : null ou {identifiant, actif, doit_changer_mot_de_passe,
 * derniere_connexion}).
 */
export default function AccesPlateforme({ base, ficheId, email, acces: accesInitial }) {
  const [acces, setAcces] = useState(accesInitial || null);
  const [secret, setSecret] = useState(null);       // { identifiant, mot_de_passe_provisoire }
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState("");
  const [copie, setCopie] = useState(false);

  const erreurDe = (err) => {
    const d = err?.response?.data;
    return d?.email || d?.detail || "L'opération n'a pas abouti. Réessayez.";
  };

  const creerOuReinitialiser = async () => {
    if (acces && !window.confirm(
      "Générer un nouveau mot de passe provisoire ? L'ancien ne fonctionnera plus.")) return;
    setEnCours(true); setErreur(""); setCopie(false);
    try {
      const r = await creerOuReinitialiserAcces(base, ficheId);
      setAcces(r.acces);
      setSecret({ identifiant: r.identifiant, mot_de_passe_provisoire: r.mot_de_passe_provisoire });
    } catch (err) {
      setErreur(erreurDe(err));
    } finally {
      setEnCours(false);
    }
  };

  const desactiver = async () => {
    if (!window.confirm("Désactiver l'accès ? La personne ne pourra plus se connecter.")) return;
    setEnCours(true); setErreur(""); setSecret(null);
    try {
      setAcces((await desactiverAcces(base, ficheId)).acces);
    } catch (err) {
      setErreur(erreurDe(err));
    } finally {
      setEnCours(false);
    }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(
        `Identifiant : ${secret.identifiant}\nMot de passe provisoire : ${secret.mot_de_passe_provisoire}\n` +
        `Connexion : ${window.location.origin}`);
      setCopie(true);
    } catch {
      setCopie(false);
    }
  };

  const derniere = acces?.derniere_connexion
    ? new Date(acces.derniere_connexion).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })
    : null;

  return (
    <div className="acces-plateforme">
      <div className="acces-titre"><i className="fa-solid fa-key"></i> Accès à la plateforme</div>

      {!acces ? (
        <p className="acces-etat">
          Pas encore d'accès.
          {!email && <> Ajoutez une adresse email à la fiche : elle servira d'identifiant.</>}
        </p>
      ) : (
        <p className="acces-etat">
          <span className={`acces-badge ${acces.actif ? "actif" : "inactif"}`}>
            {acces.actif ? "Actif" : "Désactivé"}
          </span>{" "}
          Identifiant : <strong>{acces.identifiant}</strong>
          {acces.actif && acces.doit_changer_mot_de_passe && <> · mot de passe provisoire pas encore changé</>}
          {derniere && <> · dernière connexion le {derniere}</>}
        </p>
      )}

      {secret && (
        <div className="acces-secret">
          <div>Identifiant : <strong>{secret.identifiant}</strong></div>
          <div>Mot de passe provisoire : <code>{secret.mot_de_passe_provisoire}</code></div>
          <p>
            Notez-le maintenant : il ne sera plus affiché. La personne devra le remplacer
            à sa première connexion.
          </p>
          <button type="button" className="acces-btn" onClick={copier}>
            <i className={`fa-solid ${copie ? "fa-check" : "fa-copy"}`}></i> {copie ? "Copié" : "Copier"}
          </button>
        </div>
      )}

      {erreur && <div className="acces-erreur">{erreur}</div>}

      <div className="acces-actions">
        <button type="button" className="acces-btn principal" disabled={enCours || (!acces && !email)}
                onClick={creerOuReinitialiser}>
          {enCours ? "…" : !acces ? "Créer l'accès" : acces.actif ? "Nouveau mot de passe provisoire" : "Réactiver l'accès"}
        </button>
        {acces?.actif && (
          <button type="button" className="acces-btn" disabled={enCours} onClick={desactiver}>
            Désactiver l'accès
          </button>
        )}
      </div>
    </div>
  );
}
